<template>
  <ul class="navbar-nav ms-auto">
    <li class="nav-item dropdown">
      <!--
        MK3 avatar trigger: blue initials chip + display name + chevron.
        Replaces the old FontAwesome `fa-user-circle` icon. Initials
        are computed from `user.name` (or fall back to email) so we
        always have something readable to show.
      -->
      <a
        class="nav-link user-trigger"
        href="#"
        role="button"
        @click.stop.prevent="dropdownOpen = !dropdownOpen"
      >
        <span class="user-avatar">{{ initials }}</span>
        <span class="user-label d-none d-md-block">{{ displayName }}</span>
        <i class="fa fa-chevron-down chevron" />
      </a>
      <ul
        class="dropdown-menu dropdown-menu-end user-dropdown"
        :class="{ show: dropdownOpen }"
      >
        <!--
          Header section: name (bold) above email (gray). Mirrors the
          MK3 reference UI. The email is `:title`'d so a hover tooltip
          surfaces the full address even when it visually wraps.
        -->
        <li class="user-dropdown-header">
          <div class="user-dropdown-name">
            {{ displayName }}
          </div>
          <div
            class="user-dropdown-email"
            :title="user?.email"
          >
            {{ user?.email }}
          </div>
        </li>
        <li class="user-dropdown-divider" />
        <li>
          <a
            class="dropdown-item user-dropdown-item"
            href="#"
            @click.prevent="showIntroVideo"
          >
            <i class="fa fa-play-circle" /> Introductory video
          </a>
        </li>
        <li>
          <a
            class="dropdown-item user-dropdown-item"
            href="#"
            @click.prevent="switchClient()"
          >
            <i class="fa fa-exchange" /> Switch TMC
          </a>
        </li>
        <li>
          <a
            class="dropdown-item user-dropdown-item user-dropdown-logout"
            href="#"
            @click.prevent="doLogout()"
          >
            <i class="fa fa-sign-out" /> Logout
          </a>
        </li>
      </ul>
    </li>
  </ul>
  <!--
    UiModal's API is v-model:model-value (not BVN b-modal's
    visible/hide). We bind v-model so opening/closing flows through
    the standard channel, and watch the closing transition to fire
    the one-time intro-video-watched metadata update.
  -->
  <intro-video
    v-if="introVideoVisible"
    v-model="introVideoVisible"
    @update:model-value="(open: boolean) => { if (!open) onIntroHidden() }"
  />
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import router from '@/router'
  import { logout } from '@/auth/AuthService'
  import { useAccountsStore } from '@/stores/accounts'

  import IntroVideo from './IntroVideo.vue'

  defineOptions({ name: 'UserBadge' })

  const accountsStore = useAccountsStore()
  const { user } = storeToRefs(accountsStore)

  const introVideoVisible = ref(false)
  const dropdownOpen = ref(false)

  // What we render to the right of the avatar AND in the dropdown
  // header. Falls back to the local-part of the email when the
  // user has no `name` set so the chip never reads "(empty)".
  const displayName = computed(() => {
    return user.value?.name
      || user.value?.email?.split('@')[0]
      || ''
  })

  // Two-letter initials for the avatar circle.
  // - "Simon Crunden"   → "SC"   (first letter of first + last word)
  // - "simon+goldman"   → "SI"   (first two letters when no space)
  // - missing name      → first char of email, uppercased
  // - nothing at all    → "?"
  const initials = computed(() => {
    const name = (user.value?.name || '').trim()
    if (name) {
      const parts = name.split(/\s+/)
      if (parts.length > 1) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.slice(0, 2).toUpperCase()
    }
    const email = user.value?.email || ''
    return (email[0] || '?').toUpperCase()
  })

  function switchClient ( clientId? ) {
    dropdownOpen.value = false
    router.push('/client-selection')
  }

  function doLogout () {
    dropdownOpen.value = false
    logout()
  }

  // Close dropdown when clicking outside
  function onDocumentClick (e: Event) {
    const el = (e.target as HTMLElement)
    if (!el.closest('.nav-item.dropdown')) {
      dropdownOpen.value = false
    }
  }
  onMounted(() => document.addEventListener('click', onDocumentClick))
  onUnmounted(() => document.removeEventListener('click', onDocumentClick))

  function showIntroVideo () {
    dropdownOpen.value = false
    introVideoVisible.value = true
  }

  // Side-effect on close: mark the intro video as watched the first
  // time the user dismisses it. Triggered from the modal's
  // update:model-value(false) above; v-model keeps introVideoVisible
  // in sync so we don't need to flip it here.
  function onIntroHidden () {
    if (!user.value?.metadata?.intro_video_watched) {
      useAccountsStore().updateUserMetadata({'intro-video-watched': true})
    }
  }
</script>

<style lang="scss" scoped>
  // Trigger row — avatar + name + chevron, vertically centred.
  .nav-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
    padding: 0.25rem 0.75rem;
  }
  .user-label {
    cursor: pointer;
    font-weight: 500;
    color: #1f2937;            // gray-800
  }
  .user-trigger:hover .user-label {
    color: #111827;            // gray-900
  }
  .chevron {
    color: #6b7280;            // gray-500
    font-size: 0.75rem;
    margin-left: 0.125rem;
  }

  // MK3 avatar chip: blue circle, white initials.
  .user-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;            // 36px
    height: 2.25rem;
    border-radius: 9999px;
    background-color: #2563eb; // primary-600
    color: #ffffff;
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.025em;
    cursor: pointer;
    user-select: none;
  }

  // Dropdown chrome — soft shadow, rounded, capped width so a long
  // email can't push the menu off the right edge of the viewport.
  // `margin-top: 10px` adds a gap between the avatar trigger and the
  // menu so the menu doesn't sit flush against the avatar circle.
  .dropdown-menu {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 10px;
    padding-bottom: 0;
    line-height: 1.5;
    z-index: 1050;
  }
  .user-dropdown {
    min-width: 240px;
    max-width: 320px;
    padding: 0;
    border: 1px solid #e5e7eb;       // gray-200
    border-radius: 0.75rem;          // ~12px
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
                0 4px 6px -2px rgba(0, 0, 0, 0.05);
    background-color: #ffffff;
    overflow: hidden;                // clip children to rounded corners
  }

  // Header block — name + email stacked.
  .user-dropdown-header {
    padding: 0.875rem 1rem 0.75rem;
    cursor: default;
  }
  .user-dropdown-name {
    color: #111827;            // gray-900
    font-weight: 600;
    font-size: 0.9375rem;
    line-height: 1.25rem;
    word-break: break-word;
  }
  .user-dropdown-email {
    color: #6b7280;            // gray-500
    font-size: 0.8125rem;
    line-height: 1.125rem;
    margin-top: 0.125rem;
    word-break: break-all;     // long addresses wrap inside the menu
  }

  // Thin gray divider between the header and the action items.
  .user-dropdown-divider {
    height: 1px;
    background-color: #e5e7eb; // gray-200
    margin: 0;
  }

  // Action items — bigger hit area than Bootstrap default, gray text,
  // FA icon left-aligned with a fixed width so labels line up.
  .user-dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    color: #374151;            // gray-700
    text-decoration: none;
    transition: background-color 120ms ease;

    .fa {
      width: 1rem;
      text-align: center;
      font-size: 1rem;
      color: #6b7280;          // gray-500
      margin-left: 10px;       // breathing room from the menu's left edge
    }

    &:hover,
    &:focus {
      background-color: #f3f4f6; // gray-100
      color: #111827;            // gray-900
    }
  }

  // Logout — red icon and red label, matching the MK3 reference.
  .user-dropdown-logout {
    color: #dc2626;            // red-600
    .fa { color: #dc2626; }

    &:hover,
    &:focus {
      background-color: #fef2f2; // red-50
      color: #b91c1c;            // red-700
      .fa { color: #b91c1c; }
    }
  }
</style>
